import React, { useCallback } from "react"
import types from "prop-types"
import Formsy from "formsy-react"
import { isEmpty } from "lodash-es"
import {
  useCustomName,
  useModal,
  useSCIMResourceManagement,
  useSubmit,
} from "hooks"
import { DEPARTMENT_KEY } from "utils/customNameHelpers"
import { adminFacilityDepartmentShape } from "utils/propTypeShapes"
import * as API from "services/api"
import Modal, { ModalHeader } from "shared/Modal"
import { PencilIcon } from "shared/icons"
import { PrimaryButton, Button } from "shared/buttons"
import { successToast, errorToast } from "shared/toast"
import ExternalIdentifier from "./Department/Fields/ExternalIdentifier"
import Name from "./Department/Fields/Name"
import ListLastOnForms from "./Department/Fields/ListLastOnForms"

const EditDepartmentModal = ({ facilityId = undefined, department, onDepartmentUpdated }) => {
  const {
    name: departmentName,
    nameInSentence: departmentNameInSentence,
  } = useCustomName(DEPARTMENT_KEY)

  const { departmentsManagedBySCIM } = useSCIMResourceManagement()

  const { modalIsOpen, openModal, closeModal } = useModal()
  const { canSubmit, enableSubmit, disableSubmit } = useSubmit(false)

  const { id: departmentId } = department

  const updateDepartment = useCallback(async (model) => {
    const response = await API.updateFacilityDepartment({
      department: model,
      departmentId,
      facilityId,
    })

    if (response.ok) {
      const { name, externalIdentifier, listLastOnForms } = response.data

      onDepartmentUpdated({
        departmentId, name, externalIdentifier, listLastOnForms,
      })
      closeModal()
      successToast(`${departmentName} updated successfully!`)
    } else {
      console.error("Error updating department: ", response)
      errorToast(`Something went wrong. Unable to update ${departmentNameInSentence}.`, response)
    }
  }, [departmentId, facilityId, onDepartmentUpdated, closeModal, departmentName, departmentNameInSentence])

  if (!facilityId || isEmpty(department)) return null

  return (
    <>
      <div className="flex p-1">
        <button onClick={openModal}>
          <PencilIcon className="w-4 h-4 cursor-pointer" />
        </button>
      </div>
      <Modal
        isOpen={modalIsOpen}
        aria={{ label: `edit ${departmentNameInSentence}` }}
      >
        <ModalHeader closeModal={closeModal} heading={`Edit ${departmentName}`} />
        <Formsy
          onSubmit={updateDepartment}
          onValid={enableSubmit}
          onInvalid={disableSubmit}
        >
          <div className="w-3/4 mx-auto grid gap-3">
            <Name department={department} />
            {
              departmentsManagedBySCIM && <ExternalIdentifier department={department} />
            }
            <ListLastOnForms department={department} />
            <div className="flex justify-center mt-8">
              <Button
                text="Cancel"
                onClick={closeModal}
                className="border border-light-300 mr-2"
              />
              <PrimaryButton
                text="Update"
                type="submit"
                disabled={!canSubmit}
              />
            </div>
          </div>
        </Formsy>
      </Modal>
    </>
  )
}

EditDepartmentModal.propTypes = {
  facilityId: types.number,
  department: adminFacilityDepartmentShape.isRequired,
  onDepartmentUpdated: types.func.isRequired,
}

export default EditDepartmentModal
